import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, Check, X, Trash2, Search, MessageSquare, User, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";

const AdminReviewsPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");

  // Redirect non-admins
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reviewsRes, productsRes] = await Promise.all([
        axios.get(`${API}/admin/reviews`, {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
        axios.get(`${API}/products?limit=500`)
      ]);
      setReviews(reviewsRes.data || []);
      setProducts(productsRes.data?.products || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const getProductName = (productId) => {
    const product = products.find(p => p.product_id === productId);
    return product?.name || productId;
  };

  const moderateReview = async (reviewId, status) => {
    try {
      await axios.put(
        `${API}/admin/reviews/${reviewId}`,
        null,
        {
          params: { status },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success(`Review ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try {
      await axios.delete(
        `${API}/admin/reviews/${reviewId}`,
        {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Review deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const filteredReviews = reviews.filter(review => {
    // Status filter
    if (statusFilter !== "all" && review.status !== statusFilter) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesProduct = getProductName(review.product_id).toLowerCase().includes(search);
      const matchesComment = review.comment?.toLowerCase().includes(search);
      const matchesName = review.user_name?.toLowerCase().includes(search);
      const matchesEmail = review.user_email?.toLowerCase().includes(search);
      if (!matchesProduct && !matchesComment && !matchesName && !matchesEmail) {
        return false;
      }
    }
    
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`}
          />
        ))}
      </div>
    );
  };

  const statusTabs = [
    { value: "pending", label: "Pending", count: reviews.filter(r => r.status === "pending").length },
    { value: "approved", label: "Approved", count: reviews.filter(r => r.status === "approved").length },
    { value: "rejected", label: "Rejected", count: reviews.filter(r => r.status === "rejected").length },
    { value: "all", label: "All Reviews", count: reviews.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-reviews-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="container mx-auto px-4">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-primary mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MessageSquare className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 uppercase tracking-tight">
                Review Moderation
              </h1>
              <p className="text-gray-500 text-sm">
                {reviews.filter(r => r.status === "pending").length} reviews pending approval
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Search */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by product, customer name, email, or comment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Status Tabs */}
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full">
          <TabsList className="bg-white border border-gray-200 p-1 mb-4 flex-wrap h-auto gap-1">
            {statusTabs.map(tab => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="data-[state=active]:bg-primary data-[state=active]:text-white"
              >
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded ${
                  tab.value === "pending" && tab.count > 0 
                    ? "bg-red-500 text-white" 
                    : "bg-gray-100 data-[state=active]:bg-white/20"
                }`}>
                  {tab.count}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Reviews List */}
          <div className="space-y-4">
            {loading ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-2 text-gray-500">Loading reviews...</p>
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600 font-medium">No reviews found</p>
                <p className="text-gray-400 text-sm">
                  {statusFilter === "pending" ? "No reviews pending moderation" : "No reviews match your search"}
                </p>
              </div>
            ) : (
              filteredReviews.map((review) => (
                <div
                  key={review.review_id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-sm transition-shadow"
                  data-testid={`review-${review.review_id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Product */}
                      <Link
                        to={`/product/${review.product_id}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {getProductName(review.product_id)}
                      </Link>
                      
                      {/* Rating */}
                      <div className="flex items-center gap-3 mt-2">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-500">{review.rating}/5</span>
                      </div>
                      
                      {/* Comment */}
                      <p className="mt-3 text-gray-700">{review.comment}</p>
                      
                      {/* Meta */}
                      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {review.user_name || "Anonymous"}
                        </span>
                        {review.user_email && (
                          <span>{review.user_email}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(review.created_at)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          review.status === "approved" ? "bg-green-100 text-green-800" :
                          review.status === "rejected" ? "bg-red-100 text-red-800" :
                          "bg-yellow-100 text-yellow-800"
                        }`}>
                          {review.status}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {review.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => moderateReview(review.review_id, "approved")}
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => moderateReview(review.review_id, "rejected")}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </>
                      )}
                      {review.status === "approved" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-600 border-amber-300 hover:bg-amber-50"
                          onClick={() => moderateReview(review.review_id, "rejected")}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      )}
                      {review.status === "rejected" && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => moderateReview(review.review_id, "approved")}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-600"
                        onClick={() => deleteReview(review.review_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminReviewsPage;
